/**
 * Fyncall Widget Loader - Tier 1 (2KB gzipped)
 *
 * This tiny loader:
 * 1. Extracts widget_id from script tag
 * 2. Creates global Fyncall namespace with queued methods
 * 3. Exchanges widget_id for JWT token
 * 4. Loads the shim (Web Component)
 *
 * Embed code:
 * <script async src="https://cdn.fyncall.com/widget/loader.js" data-fyncall-widget="fw_xxx"></script>
 */
(function(w, d) {
  'use strict';

  // Prevent double initialization
  if (w.Fyncall && w.Fyncall._initialized) return;

  // Get configuration from script tag attributes
  const script = d.currentScript ||
    (function() {
      // Fallback: select the last <script> with data-fyncall-widget if currentScript isn’t supported
      var scripts = d.querySelectorAll('script[data-fyncall-widget]');
      return scripts.length ? scripts[scripts.length - 1] : null;
    })();

  if (!script) {
    console.error('[Fyncall] Script tag not found or missing data-fyncall-widget attribute');
    return;
  }

  // Token-based auth uses widget_id
  const widgetId = script.getAttribute('data-fyncall-widget');

  if (!widgetId) {
    console.error('[Fyncall] Missing required data-fyncall-widget attribute');
    return;
  }

  // Runtime config (injected by backend) takes precedence over build-time defaults
  // Priority: data-attribute > runtime injection > build-time default
  const env = w.__FYNCALL_ENV__ || {};
  const baseUrl = script.getAttribute('data-base-url') || env.apiUrl || '__API_BASE_URL__';
  const cdnUrl = script.getAttribute('data-cdn-url') || env.cdnUrl || '__CDN_BASE_URL__';
  const version = script.getAttribute('data-version') || null; // Allow pinning version

  // Create global namespace with command queue
  var F = w.Fyncall = w.Fyncall || {
    _q: [],
    _ready: false,
    _initialized: true,
    _config: null,
    _widgetId: widgetId,
    _token: null,
    _tokenExpiresAt: null,
    _baseUrl: baseUrl,
    _cdnUrl: cdnUrl
  };

  // Define stub API methods that queue calls until widget loads
  var methods = ['init', 'open', 'close', 'toggle', 'identify', 'track', 'on', 'off', 'setLocale', 'showLauncher', 'hideLauncher'];

  methods.forEach(function(method) {
    if (typeof F[method] !== 'function' || F._q) {
      F[method] = function() {
        F._q.push([method, Array.prototype.slice.call(arguments)]);
        return F;
      };
    }
  });

  // Exchange widget_id for JWT token
  function exchangeToken() {
    var tokenUrl = baseUrl + '/api/v1/widget/auth/token';

    console.log('[Fyncall] Exchanging widget_id for token:', widgetId);

    var headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };

    if (baseUrl.indexOf('ngrok') !== -1) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    // Get visitor_id from localStorage if exists
    var visitorId = null;
    try {
      visitorId = localStorage.getItem('fynchat_visitor_id');
    } catch (e) {
      // localStorage not available
    }

    return fetch(tokenUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        widget_id: widgetId,
        visitor_id: visitorId
      }),
      credentials: 'omit'
    })
      .then(function(response) {
        console.log('[Fyncall] Token exchange response status:', response.status);
        if (!response.ok) {
          throw new Error('Token exchange failed: ' + response.status);
        }
        return response.json();
      })
      .then(function(data) {
        console.log('[Fyncall] Token received, expires in:', data.expires_in, 'seconds');
        F._token = data.token;
        F._tokenExpiresAt = Date.now() + (data.expires_in * 1000);
        return {
          token: data.token,
          expires_in: data.expires_in,
          widget_config_id: data.widget_config_id,
          config: data.config
        };
      });
  }

  // Load the shim script
  function loadShim(widgetVersion) {
    var shimUrl = cdnUrl + '/' + widgetVersion + '/shim.js';

    var s = d.createElement('script');
    s.src = shimUrl;
    s.async = true;
    s.crossOrigin = 'anonymous';

    s.onload = function() {
      F._ready = true;
      // Shim will process the queue when it initializes
      if (w.FyncallShim && typeof w.FyncallShim.processQueue === 'function') {
        w.FyncallShim.processQueue(F._q, F._config, {
          widgetId: widgetId,
          token: F._token,
          tokenExpiresAt: F._tokenExpiresAt,
          baseUrl: baseUrl,
          cdnUrl: cdnUrl
        });
      }
    };

    s.onerror = function() {
      console.error('[Fyncall] Failed to load widget shim from:', shimUrl);
      // Try fallback to latest
      if (widgetVersion !== 'latest') {
        console.warn('[Fyncall] Attempting fallback to latest version');
        loadShim('latest');
      }
    };

    d.head.appendChild(s);
  }

  // Exchange widget_id for token and load shim
  exchangeToken()
    .then(function(result) {
      F._config = result.config || {};
      F._config.widget_config_id = result.widget_config_id;
      F._config.widget_id = widgetId;
      var widgetVersion = version || F._config.widget_version || 'latest';
      loadShim(widgetVersion);
    })
    .catch(function(err) {
      console.error('[Fyncall] Failed to exchange token:', err.message);
      console.error('[Fyncall] Widget initialization failed. Check widget_id and domain configuration.');
    });

})(window, document);
